import * as THREE from 'three/webgpu';

import { uFogColor, uFogDensity } from '../core/uniforms.js';

/**
 * @file Scene fog, driven by the day/night cycle.
 *
 * Kept deliberately dumb: `THREE.FogExp2` on the scene, mutated in place. Node
 * materials pick `scene.fog` up automatically (`NodeMaterial.setupFog`), so
 * nothing has to be re-wired per material — a material only opts OUT, via
 * `material.fog = false` (the sky dome, stars, sun/moon do exactly that).
 *
 * The `uFogColor` / `uFogDensity` uniforms from `core/uniforms.js` are mirrored
 * here so hand-written node graphs that fake their own fog (sprite batches,
 * impostors, anything that bypasses `NodeMaterial`) read the same numbers
 * instead of inventing a second source of truth.
 *
 * Density note: fog gets DENSER at night. That is not physics, it is staging —
 * it hides the draw distance when there is no sun to model the far geometry, and
 * it makes lit windows read as the only thing worth walking toward.
 *
 * @module engine/render/fog
 */

/**
 * @typedef {Object} FogHandle
 * @property {THREE.FogExp2} fog The live fog object (also `scene.fog`).
 * @property {(dayC: any, duskC: any, nightC: any) => void} setColors Re-palettes
 *   the cycle. Accepts anything `THREE.Color` accepts.
 * @property {(t: {night?: number, warm?: number, sunset?: number}) => void} setTime
 * @property {(d: number) => void} setDensity Changes the DAY density (a level
 *   may want thicker air); the night multiplier still applies on top.
 * @property {(w: {densityMul?: number, grey?: number}) => void} setWeather
 *   Weather's two knobs, STICKY across later `setTime` calls (same contract as
 *   `SkyHandle.setWeather`): `densityMul` multiplies the whole density stack —
 *   base x night x weather — and `grey` pulls the fog colour toward its own
 *   luma. Both default to their identity (1 and 0), so a scene with no Weather
 *   system has exactly the fog it always had.
 * @property {() => void} dispose Restores whatever fog the scene had. Idempotent.
 */

/** Reference palette — matches `render/sky.js`'s horizon stops. */
export const FOG_DEFAULTS = Object.freeze({
  day: '#ded0b2',
  dusk: '#e8956a',
  night: '#101c30',
});

/**
 * @param {THREE.Scene} scene
 * @param {Object} [o]
 * @param {number} [o.density=0.0035] Daytime exponential density, per metre.
 * @param {number} [o.nightDensityMul=1.45] Density multiplier at full night.
 * @param {string|number} [o.day]
 * @param {string|number} [o.dusk]
 * @param {string|number} [o.night]
 * @returns {FogHandle}
 */
export function createFog(scene, o = {}) {
  const {
    density = 0.0035,
    nightDensityMul = 1.45,
    day = FOG_DEFAULTS.day,
    dusk = FOG_DEFAULTS.dusk,
    night = FOG_DEFAULTS.night,
  } = o;

  const prevFog = scene.fog;

  const colDay = new THREE.Color(day);
  const colDusk = new THREE.Color(dusk);
  const colNight = new THREE.Color(night);
  const tmp = new THREE.Color();

  let baseDensity = density;

  const fog = new THREE.FogExp2(colDay.getHex(), baseDensity);
  scene.fog = fog;

  uFogColor.value.copy(colDay);
  uFogDensity.value = baseDensity;

  let disposed = false;

  // ── weather, sticky ─────────────────────────────────────────────────────────
  let densityMul = 1;
  let grey = 0;
  /** The last time payload, so `setWeather` can re-apply without a clock. */
  let lastTime = /** @type {{night?: number, warm?: number, sunset?: number}} */ ({});

  /** @param {{night?: number, warm?: number, sunset?: number}} [t] */
  function setTime(t = {}) {
    lastTime = t;
    const n = clamp01(t.night ?? 0);
    const s = clamp01(t.sunset ?? t.warm ?? 0);

    fog.color.copy(colDay).lerp(tmp.copy(colDusk), s).lerp(tmp.copy(colNight), n);
    // Cloud/rain/fog haze is COLOURLESS — pull the cycle's colour toward its own
    // luma rather than toward a constant, so a grey dusk is a dim orange-grey and
    // a grey midnight stays near-black instead of both landing on the same slab.
    if (grey > 0) {
      const l = fog.color.r * 0.2126 + fog.color.g * 0.7152 + fog.color.b * 0.0722;
      fog.color.lerp(tmp.setRGB(l * 0.94, l * 0.97, l * 1.02), grey);
    }
    fog.density = baseDensity * (1 + (nightDensityMul - 1) * n) * densityMul;

    uFogColor.value.copy(fog.color);
    uFogDensity.value = fog.density;
  }

  return {
    fog,

    setColors(dayC, duskC, nightC) {
      if (dayC != null) colDay.set(dayC);
      if (duskC != null) colDusk.set(duskC);
      if (nightC != null) colNight.set(nightC);
    },

    setTime,

    setDensity(d) {
      baseDensity = d;
      setTime(lastTime);
    },

    /** @param {{densityMul?: number, grey?: number}} [w] */
    setWeather(w = {}) {
      if (w.densityMul != null) densityMul = Math.max(0, w.densityMul);
      if (w.grey != null) grey = clamp01(w.grey);
      setTime(lastTime);
    },

    dispose() {
      if (disposed) return;
      disposed = true;
      // Only hand the scene back if nobody replaced our fog in the meantime.
      if (scene.fog === fog) scene.fog = prevFog ?? null;
      uFogDensity.value = 0;
    },
  };
}

/** @param {number} v */
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
